import {
    type Absence,
    type App,
    type DailyResultsDTO,
    type DateRange,
    GetAbsencesParams,
    GetDailyResultsParams,
    GetRevenueEntriesParams,
    type RevenueEntry,
    RevenueType,
    type TimeEntry,
    TimeEntryType,
} from "@pentacode/core";
import { getRangesForLocalResultCalculation } from "../../../utils/daily-results-local.util";

export const employeeTimeKey = ["employee-time"] as const;

export const employeeAdvancesKey = ["employee-advances"] as const;

export interface EmployeeTimeData {
    timeEntries: TimeEntry[];
    absences: Absence[];
    advances: RevenueEntry[];
    prevResults: DailyResultsDTO[];
}

export function employeeTimeQuery(app: App, employeeId: number, range: DateRange) {
    return {
        queryKey: [...employeeTimeKey, employeeId, range.from, range.to] as const,
        queryFn: async ({ signal }: { signal: AbortSignal }): Promise<EmployeeTimeData> => {
            const { from, to } = range;
            const ranges = getRangesForLocalResultCalculation(range, app.company.settings.startPayrollPeriod);
            const [timeEntries, absences, advances, prevResults] = await Promise.all([
                // takes no abort signal
                app.getTimeEntries({
                    ...ranges.timeEntries,
                    employee: employeeId,
                    type: [
                        TimeEntryType.Work,
                        TimeEntryType.Vacation,
                        TimeEntryType.Sick,
                        TimeEntryType.HourAdjustment,
                        TimeEntryType.VacationAdjustment,
                        TimeEntryType.CompDay,
                        TimeEntryType.ChildSick,
                        TimeEntryType.SickInKUG,
                        TimeEntryType.Free,
                    ],
                }),
                app.api.getAbsences(new GetAbsencesParams({ from, to, employee: [employeeId] }), { signal }),
                app.hasPermission("manage.employees.advances")
                    ? app.api.getRevenueEntries(
                          new GetRevenueEntriesParams({
                              from,
                              to,
                              employee: employeeId,
                              type: [RevenueType.PayAdvance],
                          }),
                          { signal }
                      )
                    : Promise.resolve([] as RevenueEntry[]),
                app.api.getDailyResults(
                    new GetDailyResultsParams({
                        ...ranges.prevDailyResults,
                        filters: [{ type: "employeeId", value: employeeId }],
                    }),
                    { signal }
                ),
            ]);
            return { timeEntries, absences, advances, prevResults };
        },
    };
}

export function employeeAdvancesQuery(app: App, employeeId: number, range: DateRange) {
    return {
        queryKey: [...employeeAdvancesKey, employeeId, range.from, range.to] as const,
        queryFn: async ({ signal }: { signal: AbortSignal }): Promise<RevenueEntry[]> =>
            (
                await app.api.getRevenueEntries(
                    new GetRevenueEntriesParams({ ...range, type: [RevenueType.PayAdvance], employee: employeeId }),
                    { signal }
                )
            ).filter((e) => !e.draft),
    };
}
